/** Supported RR types in the visual table (others stay in raw / preamble). */
export type DnsRowType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT';

export interface DnsRecordRow {
  id: string;
  host: string;
  ttl: string;
  type: DnsRowType;
  value: string;
  mxPriority?: string;
}

let _rid = 0;
const rid = () => `r${++_rid}`;

/** Strip inline ; comments (outside quotes — best-effort). */
function stripComment(line: string): string {
  const i = line.indexOf(';');
  if (i < 0) return line;
  return line.slice(0, i).trimEnd();
}

/**
 * Split zone file into preamble (through SOA closing paren) + body lines.
 */
export function splitZonePreamble(lines: string[]): { preambleLines: string[]; bodyLines: string[] } {
  const preambleLines: string[] = [];
  let i = 0;
  let inSoa = false;
  let depth = 0;

  for (; i < lines.length; i++) {
    const raw = lines[i];
    preambleLines.push(raw);
    const line = stripComment(raw).trim();
    if (!inSoa && /\bSOA\b/i.test(line)) {
      inSoa = true;
      depth = (line.match(/\(/g) || []).length - (line.match(/\)/g) || []).length;
    } else if (inSoa) {
      depth += (line.match(/\(/g) || []).length - (line.match(/\)/g) || []).length;
      if (depth <= 0) {
        i += 1;
        break;
      }
    }
  }

  return { preambleLines, bodyLines: lines.slice(i) };
}

/**
 * Parse one body line into a row, or null if not a simple A/AAAA/CNAME/MX/TXT.
 */
export function tryParseRecordLine(line: string): DnsRecordRow | null {
  const t = stripComment(line).trim();
  if (!t) return null;

  // name  TTL  IN  TYPE  rdata...
  // name  TTL  TYPE  rdata  (no IN)
  // @     IN  A  1.2.3.4
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length < 3) return null;

  let idx = 0;
  const name = parts[idx++];

  let ttl = '3600';
  let hasIn = false;
  if (/^\d+[smhdwSMHDW]?$/.test(parts[idx])) {
    ttl = parts[idx++];
  }
  if (parts[idx]?.toUpperCase() === 'IN') {
    hasIn = true;
    idx++;
  }

  const type = (parts[idx] || '').toUpperCase() as DnsRowType;
  if (!['A', 'AAAA', 'CNAME', 'MX', 'TXT'].includes(type)) return null;
  idx++;

  if (type === 'MX') {
    const prio = parts[idx++];
    const rest = parts.slice(idx).join(' ');
    if (!prio || rest === undefined) return null;
    return { id: rid(), host: name, ttl, type: 'MX', mxPriority: prio, value: rest };
  }

  const value = parts.slice(idx).join(' ');
  if (!value) return null;
  return { id: rid(), host: name, ttl, type: type as DnsRowType, value };
}

export interface ParsedZone {
  preamble: string;
  records: DnsRecordRow[];
  /** Lines that could not be parsed as table rows (preserved on round-trip). */
  rawTail: string[];
}

export function parseZoneFile(content: string): ParsedZone {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const { preambleLines, bodyLines } = splitZonePreamble(lines);
  const preamble = preambleLines.join('\n');
  const records: DnsRecordRow[] = [];
  const rawTail: string[] = [];

  for (const line of bodyLines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(';')) continue;
    const row = tryParseRecordLine(line);
    if (row) records.push(row);
    else rawTail.push(line);
  }

  return { preamble, records, rawTail };
}

function formatRecord(r: DnsRecordRow): string {
  const host = r.host.trim() || '@';
  const ttl = r.ttl.trim() || '3600';
  if (r.type === 'MX') {
    const prio = r.mxPriority?.trim() || '10';
    return `${host}\t${ttl}\tIN\tMX\t${prio}\t${r.value.trim()}`;
  }
  return `${host}\t${ttl}\tIN\t${r.type}\t${r.value.trim()}`;
}

export function serializeZone(parsed: ParsedZone): string {
  const lines: string[] = [parsed.preamble.trimEnd()];
  if (parsed.records.length || parsed.rawTail.length) lines.push('');
  for (const r of parsed.records) lines.push(formatRecord(r));
  for (const line of parsed.rawTail) lines.push(line);
  return `${lines.join('\n')}\n`;
}

export function cloneParsedForEdit(p: ParsedZone): ParsedZone {
  return {
    preamble: p.preamble,
    records: p.records.map((r) => ({ ...r, id: rid() })),
    rawTail: [...p.rawTail],
  };
}
